// Small T3-styled inputs for the inspector. Plain, dependency-free; uses the design
// system tokens via utility classes (bg-bg-3, text-paper, border-line, bg-acid).

import type { ReactNode } from 'react'
import { ColorPickerSwatch } from './ColorPickerSwatch'

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[11px] font-medium tracking-wide text-paper-faint uppercase">
        {label}
      </span>
      {children}
    </label>
  )
}

export function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-3 border-b border-line px-4 py-4 last:border-b-0">
      <div className="text-xs font-semibold text-paper-dim">{title}</div>
      {children}
    </div>
  )
}

export function NumberSlider({
  value,
  min,
  max,
  step = 1,
  onChange,
  format,
}: {
  value: number
  min: number
  max: number
  step?: number
  onChange: (v: number) => void
  format?: (v: number) => string
}) {
  return (
    <div className="flex items-center gap-2">
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={e => onChange(Number(e.target.value))}
        className="h-1 flex-1 accent-acid"
      />
      <span className="w-12 shrink-0 text-right text-xs text-paper-dim tabular-nums">
        {format ? format(value) : value}
      </span>
    </div>
  )
}

export function Segmented<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T
  options: { value: T; label: ReactNode; title?: string }[]
  onChange: (v: T) => void
}) {
  return (
    <div className="flex overflow-hidden rounded-md border border-line">
      {options.map(o => (
        <button
          key={o.value}
          type="button"
          title={o.title}
          onClick={() => onChange(o.value)}
          className={`flex-1 px-2 py-1.5 text-xs transition-colors ${
            value === o.value
              ? 'text-ink-on-acid bg-acid'
              : 'bg-bg-3 text-paper-dim hover:text-paper'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

export function Select<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T
  options: { value: T; label: string }[]
  onChange: (v: T) => void
}) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value as T)}
      className="rounded-md border border-line bg-bg-3 px-2 py-1.5 text-sm text-paper focus:border-line-bright focus:outline-none"
    >
      {options.map(o => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  )
}

export function ColorField({
  value,
  palette,
  onChange,
}: {
  value: string
  palette: string[]
  onChange: (hex: string) => void
}) {
  const swatches = Array.from(new Set(palette.filter(Boolean)))
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {swatches.map(c => (
        <button
          key={c}
          type="button"
          title={c}
          onClick={() => onChange(c)}
          className={`h-6 w-6 rounded-full border ${
            value.toLowerCase() === c.toLowerCase() ? 'border-acid ring-1 ring-acid' : 'border-line'
          }`}
          style={{ background: c }}
        />
      ))}
      {/* Last option: a colorful circle that opens the full color picker. */}
      <ColorPickerSwatch value={value} onPick={onChange} size={24} />
    </div>
  )
}

export function ToolbarButton({
  onClick,
  disabled,
  title,
  active,
  children,
}: {
  onClick: () => void
  disabled?: boolean
  title?: string
  active?: boolean
  children: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`flex h-8 items-center gap-1.5 rounded-md border border-line px-2.5 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
        active ? 'text-ink-on-acid bg-acid' : 'bg-bg-3 text-paper-dim hover:text-paper'
      }`}
    >
      {children}
    </button>
  )
}
